import { z } from 'zod';

const commonInvalidPhoneNumberPatterns = ['1234567890', '0123456789', '9876543210'];

/**
 * Validates if the provided string is a valid US phone number.
 * Accepts formats: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890
 */
export const isValidUSPhoneNumber = (phone: string): boolean => {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // Check if the number has 10 digits (standard US phone number)
  if (digitsOnly.length !== 10) {
    return false;
  }

  // Additional validation checks for obviously invalid numbers

  // Check if all digits are the same (e.g., 0000000000, 1111111111)
  if (/^(\d)\1{9}$/.test(digitsOnly)) {
    return false;
  }

  // Check if it's all zeros
  if (digitsOnly === '0000000000') {
    return false;
  }

  // Check for invalid area codes (first digit can't be 0 or 1)
  const areaCode = digitsOnly.substring(0, 3);
  if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
    return false;
  }

  // Check for invalid exchange codes (4th digit can't be 0 or 1)
  const exchangeCode = digitsOnly.substring(3, 6);
  if (exchangeCode.startsWith('0') || exchangeCode.startsWith('1')) {
    return false;
  }

  // Check for obvious test/fake numbers
  return !commonInvalidPhoneNumberPatterns.includes(digitsOnly);
};

/**
 * Shared schema for US phone numbers with required validation
 */
export const phoneNumberSchema = z
  .string()
  .min(1, 'Phone number is required')
  .refine(val => {
    const digitsOnly = val.replace(/\D/g, '');
    return isValidUSPhoneNumber(digitsOnly);
  }, 'Please enter a valid US phone number');

/**
 * Shared schema for optional US phone numbers
 */
export const optionalPhoneNumberSchema = z
  .string()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || val.trim() === '') {
      return true;
    }

    const digitsOnly = val.replace(/\D/g, '');
    if (!isValidUSPhoneNumber(digitsOnly)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid US phone number',
      });
      return false;
    }
    return true;
  });

/**
 * Shared schema for US zip codes
 */
export const zipCodeSchema = z
  .string()
  .min(1, 'Zip code is required')
  .regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)');

/**
 * Shared schema for optional US zip codes
 */
export const optionalZipCodeSchema = z
  .string()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || val.trim() === '') {
      return true;
    }

    if (!/^\d{5}(-\d{4})?$/.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)',
      });
      return false;
    }
    return true;
  });

/**
 * Shared schema for email validation
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

/**
 * Shared schema for optional email validation
 * Accepts empty strings (treated as optional) or valid email addresses
 */
export const optionalEmailSchema = z.string().refine(
  val => {
    // Allow empty strings (treated as optional)
    if (!val || val.trim() === '') {
      return true;
    }
    // Validate email format for non-empty values
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(val);
  },
  {
    message: 'Please enter a valid email address',
  }
);

/**
 * Shared schema for city validation
 */
export const citySchema = z.string().min(2, 'City is required').max(250, 'City is too long');

/**
 * Shared schema for optional city validation
 */
export const optionalCitySchema = z
  .string()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || val.trim() === '') {
      return true;
    }

    if (val.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 2,
        type: 'string',
        inclusive: true,
        message: 'City must be at least 2 characters',
      });
      return false;
    }

    if (val.length > 250) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: 250,
        type: 'string',
        inclusive: true,
        message: 'City is too long',
      });
      return false;
    }
    return true;
  });

/**
 * Shared schema for state validation
 */
export const stateSchema = z.string().min(2, 'State is required');

/**
 * Shared schema for optional state validation
 * Validates that if a state is provided, it must be exactly 2 characters (state code)
 */
export const optionalStateSchema = z
  .string()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || val.trim() === '') {
      return true;
    }

    if (val.length !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'State code must be exactly 2 characters',
      });
      return false;
    }
    return true;
  });

/**
 * Shared schema for required address line 1
 */
export const address1Schema = z
  .string()
  .min(2, 'Address Line 1 is required')
  .max(250, 'Address Line 1 is too long');

/**
 * Shared schema for optional address line 2
 */
export const address2Schema = z.string().max(250, 'Address Line 2 is too long').optional();

/**
 * Shared schema for PO number validation
 */
export const poSchema = z
  .string()
  .min(2, 'PO# must be at least 2 characters')
  .max(25, 'PO# is too long');

/**
 * Shared schema for job name validation
 */
export const jobNameSchema = z
  .string()
  .min(2, 'Job name must be at least 2 characters')
  .max(50, 'Job name is too long');

/**
 * Shared schema for live load validation
 */
export const liveLoadSchema = z
  .number({ required_error: 'Live load is required' })
  .min(0.1, 'Must be a positive number');

/**
 * Shared schema for dead load validation
 */
export const deadLoadSchema = z
  .number({ required_error: 'Dead load is required' })
  .min(0.1, 'Must be a positive number');

/**
 * Shared schema for wind load validation.
 * ASCE-sourced values are deterministic; min 0 allows the pre-validation "--" state.
 */
export const windLoadSchema = z
  .number({ required_error: 'Wind load is required' })
  .int('Wind load must be a whole number')
  .min(0, 'Wind load cannot be negative');

/**
 * Shared schema for snow load validation.
 * ASCE may return 0 for locations with no snow load.
 */
export const snowLoadSchema = z
  .number({ required_error: 'Snow load is required' })
  .int('Snow load must be a whole number')
  .min(0, 'Snow load cannot be negative');

/**
 * Generate a date schema with custom validation messages
 *
 * @param fieldName to be used in customizing the validation message
 */
export const createDateSchema = (fieldName: string) => {
  return z
    .string()
    .min(1, `${fieldName} is required`)
    .regex(/^\d{4}-\d{2}-\d{2}$/, `${fieldName} must be in YYYY-MM-DD format`)
    .refine(dateString => {
      const date = new Date(dateString);
      return !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
    }, `Please enter a valid ${fieldName.toLowerCase()}`);
};

/**
 * Generate an optional date schema with custom validation messages
 *
 * @param fieldName to be used in customizing the validation message
 */
export const createOptionalDateSchema = (fieldName: string) => {
  return z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!val || val.trim() === '') {
        return true;
      }

      // Check format first
      if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${fieldName} must be in YYYY-MM-DD format`,
        });
        return false;
      }

      // Check if it's a valid date
      const date = new Date(val);
      if (isNaN(date.getTime()) || val !== date.toISOString().split('T')[0]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Please enter a valid ${fieldName.toLowerCase()}`,
        });
        return false;
      }

      return true;
    });
};
